import { ViewerItem } from 'presentations/api/schema/user';

// ChromeのシークレットモードなどCookieをブロックする設定の時、
// localStorage, sessionStorageを参照しただけで例外が発生するため、
// try-catchで処理するようにしています
// (yarn dev実行時はNextのエラーが出ますが、yarn startでは出ません)

declare global {
  interface Window {
    sessionStorage: any;
    localStorage: any;
  }
}

export const defaultCacheExpiration = 86400 * 30;
export const infiniteCacheExpiration = 86400 * 365 * 100;

export function getSessionItem(key: string) {
  if (typeof window === 'undefined') return;

  try {
    const cacheString = sessionStorage.getItem(key);
    if (!cacheString) return null;
    const cache = JSON.parse(cacheString);
    const sec = new Date().getTime() / 1000;
    if (cache && cache.exp > sec && cache.data) {
      return cache.data;
    }
    return null;
  } catch {
    return null;
  }
}

export function setSessionItem(key: string, value: any, expSec: number) {
  if (typeof window === 'undefined') return;
  expSec = expSec || 0;
  const now = new Date();
  const cache = {
    data: value,
    exp: now.getTime() / 1000 + expSec,
  };

  try {
    sessionStorage.setItem(key, JSON.stringify(cache));
  } catch {}
}

export function removeSessionItem(key: string) {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.removeItem(key);
  } catch {}
}

export function clearSessionItem() {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.clear();
  } catch {}
}

/**
 * LocalStorageからキーに対応した値を返します。
 * なお、exp値が切れている場合はnull(場合によってはundefined)を返します。
 *
 * @export
 * @param {string} key LocalStorageのキー
 * @param {boolean} [isMissingUndefined] ない場合にundefinedで返すかどうか
 * @return {*}
 */
export function getPersistentItem(key: string, isMissingUndefined?: boolean) {
  if (typeof window === 'undefined') return;

  try {
    const cacheString = localStorage.getItem(key);
    if (!cacheString) return isMissingUndefined ? undefined : null;
    const cache = JSON.parse(cacheString);
    const sec = new Date().getTime() / 1000;
    if (
      cache &&
      cache.exp > sec &&
      cache.data !== null &&
      cache.data !== undefined
    ) {
      return cache.data;
    }
    return null;
  } catch {
    return null;
  }
}

export function setPersistentItem(key: string, value: any, expSec: number) {
  if (typeof window === 'undefined') return;
  expSec = expSec || 0;
  const now = new Date();
  const cache = {
    data: value,
    exp: now.getTime() / 1000 + expSec,
  };

  try {
    localStorage.setItem(key, JSON.stringify(cache));
  } catch {}
}

export function removePersistentItem(key: string) {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(key);
  } catch {}
}

/**
 * LocalStorageからViewer情報に基づいたキーに対応した値を返します。
 * なお、exp値が切れている場合はnullを返します。
 *
 * @export
 * @param {string} key LocalStorageのキー
 * @param {ViewerItem} viewer ログインユーザ情報
 * @return {*}
 */
export function getPersistentItemWithViewer(
  key: string,
  viewer?: ViewerItem | null,
) {
  if (typeof window === 'undefined') return;

  // viewerが指定されていたら、team-user-keyで取得、なければkeyを取得
  // 指定されていない場合は、keyを取得
  if (viewer) {
    return (
      getPersistentItem(`${viewer.team!.uid}-${viewer.uid}-${key}`) ??
      getPersistentItem(key)
    );
  } else {
    return getPersistentItem(key);
  }
}

export function setPersistentItemWithViewer(
  key: string,
  value: any,
  expSec: number,
  viewer?: ViewerItem | null,
) {
  if (typeof window === 'undefined') return;

  // viewerが指定されていたらkeyのみのアイテムを削除(移行処理)
  if (viewer) {
    removePersistentItem(key);
  }

  setPersistentItem(
    viewer ? `${viewer.team!.uid}-${viewer.uid}-${key}` : key,
    value,
    expSec,
  );
}

export function removePersistentItemWithViewer(
  key: string,
  viewer?: ViewerItem | null,
) {
  if (typeof window === 'undefined') return;

  try {
    if (viewer) {
      localStorage.removeItem(`${viewer.team!.uid}-${viewer.uid}-${key}`);
    }
    localStorage.removeItem(key);
  } catch {}
}

export function clearPersistentItem() {
  if (typeof window === 'undefined') return;

  try {
    localStorage.clear();
  } catch {}
}

export default {
  getPersistentItem,
  setPersistentItem,
  getSessionItem,
  setSessionItem,
};
